// Native bootstrap — runs once at app start.
// Configures status bar / splash screen / push permission ONLY when the
// app is running inside a Capacitor container. In the browser it is a
// silent no-op so the same code keeps working as a PWA.

import { isNative, isIOS, isAndroid } from "./platform";

export async function initNative() {
  if (!isNative()) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: "#0A0A0A" });
    }
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch (e) {
    console.warn("[native] StatusBar init failed", e);
  }

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    // Give React ~400ms to paint the dashboard skeleton, then fade.
    setTimeout(() => SplashScreen.hide({ fadeOutDuration: 350 }), 400);
  } catch (e) {
    console.warn("[native] SplashScreen hide failed", e);
  }

  // Push registration is deferred — we only ask after the user has
  // actually enabled "Avísame por push" inside Settings/Proof of Life,
  // never on first launch (avoids gratuitous OS prompt on day 0).
  void isIOS; // placeholder so the import isn't dead-coded
}

/**
 * Idempotent push notifications setup. Call from the UI when the user
 * toggles push ON in Settings. Returns the device token on success.
 */
export async function requestPushPermission() {
  if (!isNative()) return null;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return null;
    await PushNotifications.register();
    return new Promise((resolve) => {
      const sub = PushNotifications.addListener("registration", (token) => {
        sub.then((s) => s.remove());
        resolve(token.value);
      });
    });
  } catch (e) {
    console.warn("[native] push register failed", e);
    return null;
  }
}

/**
 * Biometric unlock for the Vault. No-op on web — UI should fall back to
 * the master password flow when this returns `false`.
 */
export async function biometricUnlock(reason = "Desbloquea tu Cofre") {
  if (!isNative()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const result = await BiometricAuth.checkBiometry();
    if (!result.isAvailable) return false;
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Cancelar",
      allowDeviceCredential: true,
      iosFallbackTitle: "Usar código",
      androidTitle: "KeepSoul",
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch (e) {
    // User cancelled or failed — caller falls back to password
    return false;
  }
}
